import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography,
    useMediaQuery
} from "@mui/material";
import WordSelectionDialogView from "./WordSelectionDialogView";
import {useTheme} from "@mui/material/styles";
import {LoadingPageView} from "../../../common/components/LoadingPageView";
import {useApiRequest} from "../../../common/hooks/useApiRequest";

function DeckSettingsFormView({isBuiltinDeck, language, deck, customDeckName, customDeckWords, confirmationCallback}) {

    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [wordsLoaded, setWordsLoaded] = useState(!isBuiltinDeck);
    const [allWords, setAllWords] = useState(isBuiltinDeck ? [] : customDeckWords);
    const [deckName, setDeckName] = useState(isBuiltinDeck ? deck : customDeckName);
    const [reviewMode, setReviewMode] = useState('f2b');
    const [isManualWordSelection, setIsManualWordSelection] = useState(false);
    const [isCurrentlyManualSelecting, setIsCurrentlyManualSelection] = useState(false);
    const [selectedWords, setSelectedWords] = useState(isBuiltinDeck ? [] : customDeckWords);
    const [audioAutoGeneration, setAudioAutoGeneration] = useState(true);

    const {getRequest} = useApiRequest();

    useEffect(() => {
        if (isBuiltinDeck) {
            getRequest(`decks/builtin-decks/words?language=${language}&deck=${deck}`)
                .then(words => {
                    setWordsLoaded(true);
                    setAllWords(words);
                    setSelectedWords(words);
                });
        }
    }, []);

    if (!wordsLoaded)
        return <LoadingPageView message="Loading words..."/>

    return wordsLoaded && (
        <Box>
            {DeckNameEditor()}
            <ReviewModeSelection/>
            <WordSelection/>
            <AudioGenerationBox/>
            {isCurrentlyManualSelecting &&
                <WordSelectionDialogView words={allWords}
                                         initiallySelected={selectedWords}
                                         cancelCallback={() => setIsCurrentlyManualSelection(false)}
                                         confirmationCallback={(selection) => {
                                             setSelectedWords(selection);
                                             setIsCurrentlyManualSelection(false);
                                             setIsManualWordSelection(true);
                                         }}
                />}
            <Box width="100" margin="auto" justifyContent="center" display="flex">
                <Button variant="contained"
                        color="purple"
                        size="large"
                        sx={{borderRadius: 3, mt: 5, mb: 5, width: isMobile ? '100%' : '40%', ...(isMobile ? {} : {height: 50})}}
                        onClick={() => confirmationCallback({
                            deckName: deckName,
                            reviewMode: reviewMode,
                            words: isManualWordSelection ? selectedWords : allWords,
                            autoGenerateAudio: audioAutoGeneration
                        })}
                >
                    Click to import
                </Button>
            </Box>
        </Box>
    )

    function DeckNameEditor() {
        return (
            <div>
                <Typography variant="h5" fontWeight="bold" sx={ isMobile ? {mb: 3} : {my: 3}}>Deck name</Typography>
                <TextField variant="outlined"
                           fullWidth={true}
                           color="secondary"
                           value={deckName}
                           onChange={(e) => setDeckName(e.target.value)}
                           error={deckName.length === 0}
                />
            </div>
        )
    }

    function ReviewModeSelection() {
        return (
            <div>
                <Typography variant="h5" fontWeight="bold" sx={ isMobile ? {mb: 3} : {my: 3}}>Select review mode</Typography>
                <Grid container spacing={4}>
                    <GridOptionView title={`${language} to English`}
                                    description={`When practicing this language you will have to translate ${language} words to English`}
                                    isSelected={reviewMode === "f2b"}
                                    onSelected={() => setReviewMode("f2b")}
                    />
                    <GridOptionView title={`English to ${language}`}
                                    description={`When practicing this language you will have to translate English words to ${language}`}
                                    isSelected={reviewMode === "b2f"}
                                    onSelected={() => setReviewMode("b2f")}
                    />
                    <GridOptionView title={`Both`}
                                    description={`When practicing this language you will have to translate ${language} words to English, and then the other way around`}
                                    isSelected={reviewMode === "both"}
                                    onSelected={() => setReviewMode("both")}
                    />
                </Grid>
            </div>
        )
    }

    function WordSelection() {
        return (
            <div>
                <Typography variant="h5" fontWeight="bold" sx={{my: 3}}>Select words to add</Typography>
                <Grid container spacing={4} justifyContent="center">
                    <GridOptionView title="Everything"
                                    description={`Add all ${allWords.length} words`}
                                    isSelected={!isManualWordSelection}
                                    onSelected={() => setIsManualWordSelection(false)}
                    />
                    <GridOptionView title="Select manually"
                                    description={`Choose yourself which words to add ${isManualWordSelection ? "\n" + selectedWords.length + " selected" : ""}`}
                                    isSelected={isManualWordSelection}
                                    onSelected={() => setIsCurrentlyManualSelection(true)}
                    />
                </Grid>
            </div>
        )
    }

    function AudioGenerationBox() {
        return (
            <FormControlLabel
                control={<Checkbox color="purple" checked={audioAutoGeneration} onChange={() => setAudioAutoGeneration(!audioAutoGeneration)} />}
                label={(
                    <Typography variant="h6" fontWeight="bold">Auto generate sound for missing words</Typography>
                )}
                sx={{mt: 3}}
            />
        )
    }

    function GridOptionView({title, description, isSelected, onSelected}) {
        return (
            <Grid item xs={12} sm={4} justifyContent="center">
                <Paper elevation={5}
                       square={false}
                       onClick={onSelected}
                       sx={{backgroundColor: isSelected ? theme.palette.success.main : theme.palette.neutral.main,
                           cursor: 'pointer',
                           borderRadius: 2,
                           height: '100%',
                           p: 2,
                           transition: "transform 0.15s ease-in-out", "&:hover": {transform: "scale3d(1.05, 1.05, 1)"}}}>
                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                    <Typography variant="body1">{description}</Typography>
                </Paper>
            </Grid>
        )
    }

}

export default DeckSettingsFormView;